import { captureException } from '@sentry/react'
import { notice } from 'app/notice'
import { Button } from 'component/Button'
import { Divider, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { api } from 'store/api'
import { controller } from 'store/controller'
import { InferenceStore } from 'store/inference/InferenceStore'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { buildShareLink, buildShareText } from 'type/Inference'
import { copyText } from 'util/clipboard'
import { downloadFile } from 'util/file'
import { shareMobile } from 'util/share'
import { shareTelegram } from 'util/telegram'

type Props = {
  inference: InferenceStore
}

const Line = () => {
  return <Space height={12}>
    <Divider />
  </Space>
}

export const ActionsContent = observer(({ inference }: Props) => {
  const { tg, safari, nativeWebView, mobile } = store
  const { inference_id, url } = inference
  const link = buildShareLink(inference_id, inference.links)
  const text = buildShareText(inference.product, inference.brand, inference.gender)
  const initData = store.tgWebAppData
  const download = !opt.inline && !opt.foo && !tg && !safari && !nativeWebView
  const copy = !opt.foo
  const share = !opt.inline && !opt.foo
  const some = download || copy || tg || share
  const delete_allowed = opt.options.delete_allowed !== false

  // https://window.dresscode.ai/victoria-yatsenko-outfits
  const force_share = opt.company === '147cf1c9-b9c6-4367-968b-397c16b22aa3'

  const onDownload = useLatestCallback(async () => {
    controller.onShare('download', inference)
    store.carousel.actions = false
    if (url && await downloadFile(url)) notice.success(i18n('SharedSuccessfully'))
  })

  const onCopyLink = useLatestCallback(async () => {
    controller.onShare('link', inference)
    store.carousel.actions = false
    if (await copyText(link)) notice.success(i18n('SharedSuccessfully'))
  })

  const onShareTelegram = useLatestCallback(async () => {
    controller.onShare('bot', inference)
    store.carousel.actions = false
    const inference_id = inference.inference_id
    try {
      if (initData) {
        await api.shareInference(initData, inference_id)
        notice.success(i18n('SharedSuccessfully'))
        return
      }
    } catch (e) {
      console.error(e)
      const product = opt.options.product
      captureException(e, { extra: { initData, product, inference_id } })
    }
    shareTelegram(link, text)
    notice.success(i18n('SharedSuccessfully'))
  })

  const onOpenShare = useLatestCallback(async () => {
    if (mobile && !force_share) {
      const origin = store.meta?.location
      controller.onShare('mobile', inference)
      if (url && await shareMobile(url, link, origin)) notice.success(i18n('SharedSuccessfully'))
    } else {
      controller.onShare('qr', inference)
      store.carousel.actions = false
      store.carousel.share = true
    }
  })

  const onDelete = useLatestCallback(async () => {
    controller.deleteInference(inference)
    store.carousel.actions = false
  })

  return <Vertical>
    {download && <Button gap={8} large link action={onDownload}>
      <Icon size={20} name="download" />
      <Typo upper>
        {i18n('DownloadOutfit')}
      </Typo>
    </Button>}
    {copy && <Button gap={8} large link action={onCopyLink}>
      <Icon size={20} name="link" />
      <Typo upper>
        {i18n('CopyLink')}
      </Typo>
    </Button>}
    {tg && <Button gap={8} large link action={onShareTelegram}>
      <Icon name="ios_share" size={20} />
      <Typo>{initData ? i18n('ShareInTelegramBot') : i18n('ShareInTelegram')}</Typo>
    </Button>}
    {share && <Button gap={8} large link action={onOpenShare}>
      <Icon name="ios_share" size={20} />
      <Typo upper>
        {i18n('Share')}
      </Typo>
    </Button>}
    {delete_allowed && <>
      {some && <Line />}
      <Button gap={8} large link action={onDelete}>
        <Icon name="delete" size={20} />
        <Typo upper>
          {i18n('DeleteOutfit')}
        </Typo>
      </Button>
    </>}
  </Vertical>
})
